import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_DOCUMENT_TEMPLATES } from '../../graphql/queries';
import { UPDATE_DOCUMENT_TEMPLATE } from '../../graphql/mutations';
import { renderTemplate } from '../../utils/templateRenderer';
import { clearTemplateCache } from '../../utils/documentTemplateCache';
import {
  DOCUMENT_TEMPLATE_PLACEHOLDERS,
  type DocType,
  type PlaceholderDescriptor,
} from '../../constants/documentTemplatePlaceholders';
import { DOCUMENT_TEMPLATE_SAMPLE_FIXTURE } from '../../constants/documentTemplateSampleFixture';
import { Spinner } from '../common/Spinner';

interface DocumentTemplateRow {
  docType: DocType;
  content: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
  updatedBy: string;
  updatedByName: string;
}

/**
 * The set of valid `:docType` URL params. Derived from the placeholder
 * catalog keys so the list of supported doc types is a single source of
 * truth across the feature.
 */
const VALID_DOC_TYPES = Object.keys(DOCUMENT_TEMPLATE_PLACEHOLDERS) as DocType[];

/**
 * Type guard: does the URL param correspond to a known doc type?
 */
const isDocType = (value: string | undefined): value is DocType =>
  !!value && (VALID_DOC_TYPES as string[]).includes(value);

const PREVIEW_DEBOUNCE_MS = 300;
const LIST_PATH = '/admin/document-templates';

/**
 * 제안서/계약서 데이터 매핑 — per-template editor page.
 *
 * Mounted at `/admin/document-templates/:docType`. Routing (App.tsx) is
 * responsible for wrapping this page in `AdminLayout` and enforcing the
 * `document_templates:manage` permission, so the component only owns the
 * three-pane editor itself (textarea / placeholder panel / preview) plus
 * the save / cancel flows.
 *
 * Behaviour summary (see design §6.6 and Req 6.1–6.9):
 * - Textarea holds the editable HTML with `{{path}}` tokens.
 * - Side panel lists the placeholder catalog for the current doc type.
 * - Bottom preview pane renders `renderTemplate(textarea, FIXTURE)` into
 *   a sandboxed iframe (`allow-same-origin` only) so template CSS renders
 *   while any inline scripts stay inert.
 * - Preview updates are debounced 300ms off the last keystroke.
 * - Save invokes `updateDocumentTemplate`, clears the per-session template
 *   cache, toasts success, and navigates back to the list.
 * - Cancel and beforeunload both prompt when the textarea is dirty.
 */
const DocumentTemplateEditorPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const { docType: docTypeParam } = useParams<{ docType: string }>();

  // Reject unknown doc types with a redirect — the route guard handles
  // auth, but the URL param itself can still be anything. Replace so the
  // invalid URL does not sit in browser history.
  if (!isDocType(docTypeParam)) {
    return <Navigate to={LIST_PATH} replace />;
  }
  const docType: DocType = docTypeParam;

  const { data, loading, error: queryError } = useQuery<{
    getDocumentTemplates: DocumentTemplateRow[];
  }>(GET_DOCUMENT_TEMPLATES, { fetchPolicy: 'cache-and-network' });

  const row = useMemo<DocumentTemplateRow | undefined>(
    () => data?.getDocumentTemplates?.find((r) => r.docType === docType),
    [data, docType],
  );

  // The textarea is a locally-controlled input. `originalContent` tracks
  // the last value we synced from the server (fetched row or successful
  // save) so we can compute the dirty bit without re-reading Apollo.
  const [textareaValue, setTextareaValue] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string | null>(null);

  // Initial + refetch sync: whenever the fetched row changes and we have
  // not yet seeded the textarea, fill both the editing buffer and the
  // baseline with the server value. `cache-and-network` can fire twice,
  // so only seed when `originalContent` is still null.
  useEffect(() => {
    if (row && originalContent === null) {
      setTextareaValue(row.content);
      setOriginalContent(row.content);
    }
  }, [row, originalContent]);

  const isDirty = originalContent !== null && textareaValue !== originalContent;

  // --- Debounced preview ---------------------------------------------------
  //
  // The iframe `srcDoc` is driven by `previewHtml`, which trails the
  // textarea by `PREVIEW_DEBOUNCE_MS`. A ref holds the pending timer so
  // subsequent keystrokes cancel the previous schedule before setting a
  // new one — standard leading-keystroke debounce.
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setPreviewHtml(renderTemplate(textareaValue, DOCUMENT_TEMPLATE_SAMPLE_FIXTURE));
      debounceTimerRef.current = null;
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [textareaValue]);

  // --- beforeunload guard --------------------------------------------------
  //
  // Browser back / tab close / reload path. We attach the listener only
  // while dirty and strip it on unmount, so a clean editor does not
  // trigger the native "leave site?" dialog on unrelated navigations.
  // The native dialog text is browser-controlled; returning a non-empty
  // string is enough to trigger it on all evergreen browsers.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Legacy Chrome requires a returnValue; modern browsers ignore the
      // string but still show a generic warning when preventDefault fires.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // --- Save / Cancel -------------------------------------------------------

  const [mutationError, setMutationError] = useState<string | null>(null);

  const [updateDocumentTemplate, { loading: saving }] = useMutation(UPDATE_DOCUMENT_TEMPLATE);

  const handleSave = useCallback(async () => {
    setMutationError(null);
    try {
      await updateDocumentTemplate({
        variables: { docType, content: textareaValue },
      });
      // Mark the buffer clean BEFORE navigating so the unmount path does
      // not re-trigger the beforeunload handler via a stale dirty state,
      // and so the confirm-dialog in the navigate-back isn't tripped.
      setOriginalContent(textareaValue);
      clearTemplateCache();
      toast.success(t('admin:documentTemplates.saveSuccess'));
      navigate(LIST_PATH);
    } catch (err) {
      // Apollo throws on mutation failure; capture the message for the
      // inline banner. Do NOT navigate — the user's edits stay in the
      // textarea so they can retry without losing work.
      const message = err instanceof Error ? err.message : String(err);
      setMutationError(message);
    }
  }, [docType, textareaValue, updateDocumentTemplate, t, navigate]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm(
        t('admin:documentTemplates.unsavedChangesPrompt'),
      );
      if (!confirmed) return;
    }
    navigate(LIST_PATH);
  }, [isDirty, navigate, t]);

  const placeholders: PlaceholderDescriptor[] = DOCUMENT_TEMPLATE_PLACEHOLDERS[docType];

  // --- Render --------------------------------------------------------------

  const headerTitle = `${t('admin:documentTemplates.editorTitle')} — ${t(
    `admin:documentTemplates.docType.${docType}`,
    docType,
  )}`;

  return (
    <div className="min-h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="hover:text-blue-600 transition-colors"
          >
            {t('common:profileDropdown.adminDashboard')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <button
            type="button"
            onClick={handleCancel}
            className="hover:text-blue-600 transition-colors"
          >
            {t('admin:documentTemplates.sidebarLabel')}
          </button>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">
            {t(`admin:documentTemplates.docType.${docType}`, docType)}
          </span>
        </nav>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 pb-16 space-y-4">
        {/* Title + action bar */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">{headerTitle}</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('admin:documentTemplates.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading || originalContent === null}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {t('admin:documentTemplates.save')}
            </button>
          </div>
        </div>

        {/* Query load error */}
        {queryError && !row && (
          <div className="bg-white rounded-xl border border-red-200 p-4 text-sm text-red-600">
            {queryError.message}
          </div>
        )}

        {/* Mutation error banner (Req 6.7) */}
        {mutationError && (
          <div
            className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700"
            role="alert"
          >
            {mutationError}
          </div>
        )}

        {/* Loading state — only show spinner on the very first fetch */}
        {loading && originalContent === null ? (
          <div className="bg-white rounded-xl border border-gray-100 p-16 flex justify-center">
            <Spinner size="medium" />
          </div>
        ) : (
          <>
            {/* Upper two-column: textarea + placeholder panel */}
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
              {/* Textarea */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <textarea
                  className="w-full h-[600px] p-4 font-mono text-sm text-gray-800 outline-none resize-none focus:ring-2 focus:ring-blue-500"
                  value={textareaValue}
                  onChange={(e) => setTextareaValue(e.target.value)}
                  spellCheck={false}
                  aria-label={t('admin:documentTemplates.editorTitle')}
                />
              </div>

              {/* Placeholder reference side panel */}
              <aside className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h2 className="text-sm font-semibold text-gray-800">
                    {t('admin:documentTemplates.placeholderPanelHeading')}
                  </h2>
                </div>
                <div className="max-h-[600px] overflow-y-auto divide-y divide-gray-100">
                  {placeholders.map((p) => {
                    const token = `{{${p.path}}}`;
                    return (
                      <div
                        key={p.path}
                        className="px-4 py-2 flex items-start justify-between gap-3 hover:bg-gray-50"
                      >
                        <div className="min-w-0">
                          <code className="block font-mono text-xs text-blue-700 break-all">
                            {token}
                          </code>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {p.description}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            void navigator.clipboard?.writeText(token);
                          }}
                          className="shrink-0 text-xs text-gray-500 hover:text-blue-600"
                          aria-label={`Copy ${token}`}
                        >
                          복사
                        </button>
                      </div>
                    );
                  })}
                </div>
              </aside>
            </div>

            {/* Preview pane (full width under the editor) */}
            <section className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="text-sm font-semibold text-gray-800">
                  {t('admin:documentTemplates.previewHeading')}
                </h2>
              </div>
              <iframe
                // `allow-same-origin` only — no `allow-scripts`. This lets the
                // template CSS render (matches PDF layout) while any inline
                // <script> or on* handler in the template stays inert, and
                // the template CSS cannot leak into the admin shell's styles.
                sandbox="allow-same-origin"
                title={t('admin:documentTemplates.previewHeading')}
                srcDoc={previewHtml}
                className="w-full h-[700px] border-0 bg-white"
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentTemplateEditorPage;
